/**
 * Централизованный source-of-truth для статус-маппингов.
 * Извлечено из дублей в ArrivalsPage, ArrivalWarehousePage, ProcessingListPage,
 * MarkingPage, etc. (Phase 9 F1 part 2)
 */

// ─── Arrivals (приёмки) ───
export const ARRIVAL_STATUS_LABELS = {
  NEW: 'Новая',
  // Принята по данным поставщика, склад ещё не пересчитал. Одно название и в строке,
  // и во вкладке фильтра (раньше «Принята» в строке и «Ждут пересчёта» во вкладке).
  CONDUCTED: 'Ждёт пересчёта',
  COMPLETED: 'Завершена',
  ARCHIVE: 'Архив',
};

/* ═══════════════════════════════════════════════════════════════════════════
   ЕДИНЫЙ СПРАВОЧНИК СТАТУСОВ для <StatusBadge entity="…" status="…" />.

   Одна подпись (обычным регистром, без эмодзи) и один тон на статус:
     neutral — черновик/архив/ждёт, info — создан/в пути, warning — в работе/
     требует внимания, success — готово/отгружено, danger — отмена/ошибка,
     accent — выделить (редко).
   Страницы со своими картами (PickupsPage, TrucksPage, OzonFbsPage, MarkingPage,
   PvzReturnsListPage, ArrivalBoxesPage, ProcessingListPage…)
   переводятся на справочник при миграции страницы — подписи здесь взяты из них.
   ARRIVAL_STATUS_LABELS выше оставлен: его ещё берёт ArrivalsPage.
   ═══════════════════════════════════════════════════════════════════════════ */

const s = (label, tone) => ({ label, tone });

export const STATUSES = {
  // Приёмка (arrivals.status)
  arrival: {
    NEW: s('Новая', 'info'),
    CONDUCTED: s('Ждёт пересчёта', 'warning'),
    COMPLETED: s('Завершена', 'success'),
    ARCHIVE: s('Архив', 'neutral'),
    CANCELLED: s('Отменена', 'danger'),
  },
  // Забор у поставщика (arrivals.pickup_status)
  pickup: {
    PENDING: s('Ожидает', 'warning'),
    IN_PROGRESS: s('В пути', 'info'),
    DONE: s('Забран', 'success'),
    CANCELLED: s('Отменён', 'danger'),
  },
  // Короб приёмки (storage_boxes / arrival boxes)
  arrival_box: {
    NEW: s('Новый', 'neutral'),
    OPEN: s('Открыт', 'info'),
    IN_PROGRESS: s('Проверяется', 'warning'),
    OK: s('Проверен', 'success'),
    ISSUE: s('Расхождения', 'danger'),
    EMPTY: s('Пустой', 'neutral'),
    ARCHIVED: s('В архиве', 'neutral'),
  },
  // Отгрузка / поставка (shipments.status) — и ФБО, и ФБС-заказ
  shipment: {
    DRAFT: s('Черновик', 'neutral'),
    IN_PROGRESS: s('В работе', 'warning'),
    COMPLETED: s('Собрана', 'success'),
    SHIPPED: s('Отгружена', 'info'),
    CANCELLED: s('Отменена', 'danger'),
  },
  // Стадия обработки позиции (item_stage_progress.status)
  stage: {
    NOT_STARTED: s('Не начата', 'neutral'),
    IN_PROGRESS: s('В работе', 'warning'),
    COMPLETED: s('Готово', 'success'),
  },
  // Заказ клиента (orders.status) + статусы заказов, пришедших из MPFit
  order: {
    PENDING: s('Ожидает', 'neutral'),
    NEW: s('Новый', 'info'),
    PRODUCTS_RESERVED: s('Резерв', 'info'),
    EQUIPMENT: s('Комплектуется', 'warning'),
    READY: s('Готов', 'success'),
    READY_TO_SHIP: s('К отгрузке', 'warning'),
    SENT: s('Отправлен', 'info'),
    SHIPPED: s('Отгружен', 'success'),
    DELIVERY: s('Доставка', 'info'),
    COMPLETE: s('Завершён', 'success'),
    ARCHIVE: s('Архив', 'neutral'),
    CANCELLED: s('Отменён', 'danger'),
    // Статусы заказов MPFit на отгрузке (shipments.external_status)
    REJECT: s('Отклонён', 'danger'),
    CANCEL: s('Отменён', 'danger'),
    // Заявка на отгрузку (OrdersPage): проверка и передача складу
    VALIDATING: s('Проверка', 'info'),
    VALIDATION_ERROR: s('Ошибки', 'warning'),
    SEND_ERROR: s('Ошибка', 'danger'),
  },
  // Код ЧЗ (marking_codes.status)
  kiz: {
    AVAILABLE: s('Свободен', 'neutral'),
    RESERVED: s('Зарезервирован', 'warning'),
    PRINTED: s('Напечатан', 'info'),
    APPLIED: s('Нанесён', 'success'),
    SHIPPED: s('Отгружен', 'success'),
  },
  // Агрегат кодов ЧЗ (marking_aggregations.status)
  kiz_aggregation: {
    OPEN: s('Открыт', 'info'),
    CLOSED: s('Закрыт', 'success'),
    SHIPPED: s('Отгружен', 'success'),
  },
  // Сборочное задание WB FBS — статус продавца (shipments.wb_supplier_status)
  wb_fbs_supplier: {
    new: s('Новое', 'info'),
    confirm: s('На сборке', 'warning'),
    complete: s('В доставке', 'success'),
    cancel: s('Отменено продавцом', 'danger'),
  },
  // Сборочное задание WB FBS — статус WB (shipments.wb_status)
  wb_fbs: {
    waiting: s('Ждёт приёмки WB', 'warning'),
    sorted: s('Отсортировано', 'info'),
    sold: s('Получено покупателем', 'success'),
    canceled: s('Отменено', 'danger'),
    canceled_by_client: s('Отменено покупателем', 'danger'),
    declined_by_client: s('Отказ при получении', 'danger'),
    defect: s('Отменено: брак', 'danger'),
    ready_for_pickup: s('Прибыло в ПВЗ', 'success'),
    postponed_delivery: s('Доставка перенесена', 'warning'),
  },
  // Отправление Ozon FBS (shipments.ozon_posting_status)
  ozon_fbs: {
    awaiting_registration: s('Регистрируется', 'neutral'),
    acceptance_in_progress: s('Идёт приёмка', 'info'),
    awaiting_approve: s('Ждёт подтверждения', 'warning'),
    awaiting_packaging: s('Ждёт сборки', 'warning'),
    awaiting_deliver: s('Собран, ждёт отгрузки', 'info'),
    arbitration: s('Спор', 'danger'),
    client_arbitration: s('Спор с покупателем', 'danger'),
    delivering: s('В доставке', 'info'),
    driver_pickup: s('У водителя', 'info'),
    delivered: s('Доставлен', 'success'),
    cancelled: s('Отменён', 'danger'),
    not_accepted: s('Не принят на СЦ', 'danger'),
  },
  // Возврат с ПВЗ (pvz_return_sessions.status)
  pvz_return: {
    OPEN: s('Открыта', 'info'),
    CLOSED: s('Закрыта', 'success'),
    CANCELLED: s('Отменена', 'danger'),
  },
  // Рейс (trucks.status)
  truck: {
    planned: s('Планируется', 'neutral'),
    loading: s('Загрузка', 'warning'),
    departed: s('Уехал', 'info'),
    closed: s('Закрыт', 'success'),
  },
  // Расхождение позиции отгрузки (shipment_item_discrepancies.reason)
  discrepancy: {
    DEFECT: s('Брак', 'danger'),
    REASSORT: s('Пересорт', 'warning'),
    SHORTAGE: s('Недостача', 'warning'),
    OTHER: s('Другое', 'neutral'),
    SUBSTITUTION: s('Подмена', 'accent'),
  },
  // Выплата зарплаты (payroll_payments.status)
  payroll_payment: {
    planned: s('План', 'neutral'),
    partial: s('Частично', 'warning'),
    paid: s('Оплачено', 'success'),
    adesk_synced: s('В Adesk', 'info'),
    deferred: s('В плановых', 'neutral'),
  },
  // Принт-агент (print_agents.status)
  print_agent: {
    ONLINE: s('На связи', 'success'),
    OFFLINE: s('Не на связи', 'neutral'),
  },
  // Склад Ozon FBS в кабинете клиента (ozon warehouse.status)
  ozon_warehouse: {
    created: s('активен', 'success'),
    new: s('активируется', 'info'),
    disabled: s('в архиве', 'neutral'),
    blocked: s('заблокирован', 'danger'),
    disabled_due_to_limit: s('на паузе', 'warning'),
    error: s('ошибка', 'danger'),
  },
  // Сделка CRM (crm_deals.status)
  crm_deal: {
    ACTIVE: s('В работе', 'info'),
    WON: s('Успех', 'success'),
    LOST: s('Отказ', 'danger'),
  },
  // Статья базы знаний (knowledge_articles.is_published → published / draft)
  kb_article: {
    published: s('Опубл.', 'success'),
    draft: s('Черновик', 'warning'),
  },
  // Сопоставление кода ЧЗ с товаром при массовой загрузке (MarkingPage, bulk)
  kiz_match: {
    exact: s('Точное', 'success'),
    history: s('По истории — проверьте', 'info'),
    by_name: s('По описанию — проверьте', 'info'),
    confirmed: s('Подтверждено вами', 'success'),
    guess: s('Предположительно — проверьте', 'warning'),
    unmatched: s('Товар не найден', 'warning'),
    ambiguous: s('Неоднозначно', 'warning'),
    duplicate_db: s('Уже в системе', 'neutral'),
    duplicate_file: s('Дубль в файле', 'neutral'),
    invalid: s('Невалидный', 'danger'),
    incomplete: s('Неполный (нет крипто)', 'danger'),
  },
  // Задание импорта товаров из файла (ProductManagementPage)
  import_job: {
    PENDING: s('Ожидает', 'neutral'),
    PROCESSING: s('Выполняется', 'info'),
    COMPLETED: s('Завершено', 'success'),
    COMPLETED_WITH_ERRORS: s('Завершено с ошибками', 'warning'),
    FAILED: s('Ошибка', 'danger'),
  },
  // Подключение маркетплейса (marketplace_configs.is_active)
  mp_config: {
    ACTIVE: s('Активно', 'success'),
    INACTIVE: s('Неактивно', 'neutral'),
  },
  // Пользователь (users.is_active)
  user: {
    ACTIVE: s('Активен', 'success'),
    INACTIVE: s('Неактивен', 'neutral'),
  },
  // Заявка на пополнение баланса (billing topup_requests.status)
  topup_request: {
    PENDING: s('Ожидает', 'warning'),
    APPLIED: s('Применена', 'success'),
    REJECTED: s('Отклонена', 'danger'),
    REVERSED: s('Отменена (сторно)', 'danger'),
  },
  // Запуск списания хранения (billing storage runs.status)
  storage_run: {
    SUCCESS: s('Успех', 'success'),
    PARTIAL: s('Частично', 'warning'),
    RUNNING: s('В процессе', 'warning'),
  },
  // Задание печати (print_jobs.status)
  print_job: {
    PENDING: s('Ожидание', 'warning'),
    QUEUED: s('В очереди', 'warning'),
    PRINTING: s('Печатается', 'info'),
    COMPLETED: s('Готово', 'success'),
    FAILED: s('Ошибка', 'danger'),
    CANCELLED: s('Отменено', 'neutral'),
  },
  // Прогон синхронизации кабинета маркетплейса (marketplace_sync_logs.status)
  mp_sync_log: {
    success: s('Успешно', 'success'),
    partial: s('Частично', 'warning'),
    error: s('Ошибка', 'danger'),
    running: s('Идёт', 'info'),
    skipped: s('Не требуется', 'neutral'),
    interrupted: s('Прерван', 'warning'),
  },
  // Отправка уведомления ботом (notification_logs.status)
  notification_log: {
    SENT: s('Отправлено', 'success'),
    FAILED: s('Ошибка', 'danger'),
  },
  // Важность косяка сотрудника (violations.severity)
  violation_severity: {
    HIGH: s('Высокая', 'danger'),
    MED: s('Средняя', 'warning'),
    LOW: s('Низкая', 'neutral'),
  },
  // Короб хранения в ячейке (storage_boxes.status) — «Реестр зоны», «Где лежит»
  storage_box: {
    OPEN: s('С товаром', 'info'),
    EMPTY: s('Пустой', 'neutral'),
    ARCHIVED: s('В архиве', 'neutral'),
  },
  // Проверка хвоста ФБС сканом (fbs_tail_checks.status)
  fbs_tail_check: {
    IN_PROGRESS: s('Идёт проверка', 'warning'),
    AWAITING: s('Ждёт подтверждения', 'warning'),
    CONFIRMED: s('Выпущено', 'success'),
    CANCELLED: s('Отменена', 'neutral'),
  },
  // Позиция резерва кабинета ФБС («Хвосты» → «Резерв»): что можно сделать с удержанным
  fbs_reserve: {
    ready: s('Можно выпустить', 'success'),
    mismatch: s('Учёт ≠ коды', 'warning'),
    offshelf: s('Разложено не всё', 'warning'),
    released: s('Выпущено', 'info'),
  },
};

/**
 * Подпись и тон статуса. Неизвестный статус — как есть, нейтральным
 * (лучше увидеть код, чем пустой бейдж), пустой — «—».
 */
export function getStatus(entity, status) {
  if (status === null || status === undefined || status === '') return { label: '—', tone: 'neutral' };
  const map = STATUSES[entity] || {};
  return map[status] || map[String(status).toUpperCase()] || map[String(status).toLowerCase()]
    || { label: String(status), tone: 'neutral' };
}

export const statusLabel = (entity, status) => getStatus(entity, status).label;

// Статусы отгрузки словами клиента (аудит №4): «Черновик» и «Собрана» — складская
// кухня. Черновик = заявка принята, сборку склад ещё не начал. Список и карточка
// отгрузки клиента берут подписи отсюда.
export const CLIENT_SHIPMENT_STATUS = {
  DRAFT: 'Ждёт сборки',
  IN_PROGRESS: 'Собираем',
  COMPLETED: 'Готово к отгрузке',
  SHIPPED: 'Отгружено',
  CANCELLED: 'Отменена',
};
